import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { api } from '../App';
import { BookOpen, LogOut, User, BarChart3, MessageSquare, FileText, Award } from 'lucide-react';

export default function EmployeeDashboard({ user, logout, setUser }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [hasAssessment, setHasAssessment] = useState(false);
  const [report, setReport] = useState(null);

  useEffect(() => {
    checkAssessmentStatus();
  }, []);

  const checkAssessmentStatus = async () => {
    try {
      const response = await api.get('/reports/my');
      if (response.data && response.data.length > 0) {
        setHasAssessment(true);
        setReport(response.data[0]);
      }
    } catch (error) {
      console.error('Failed to check assessment:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStopImpersonation = async () => {
    try {
      const response = await api.post('/impersonate/stop');
      
      const { access_token, user: adminUser } = response.data;
      localStorage.setItem('access_token', access_token);
      api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setUser(adminUser);
      
      toast.success('Süper admin hesabınıza döndünüz');
      navigate('/');
    } catch (error) {
      console.error('Stop impersonation failed:', error);
      toast.error('Oturum sonlandırılamadı');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-emerald-50">
      {/* Header */}
      <header className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-cyan-600 to-blue-600 rounded-xl flex items-center justify-center">
              <BookOpen className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-800">Mizaç360</h1>
              <p className="text-sm text-slate-600">Çalışan Paneli</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {user.impersonation?.active && (
              <Badge className="bg-orange-100 text-orange-700 animate-pulse">
                Taklit Modu: {user.impersonation.admin_name}
              </Badge>
            )}
            <Badge className="bg-cyan-100 text-cyan-700">Çalışan</Badge>
            <span className="text-sm text-slate-600">{user.name}</span>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => navigate('/coach')}
              className="bg-gradient-to-r from-blue-50 to-emerald-50 border-blue-200 hover:bg-blue-100"
            >
              💬 AI Koç
            </Button>
            {user.impersonation?.active ? (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleStopImpersonation}
                className="bg-orange-50 border-orange-200"
              >
                Taklidi Durdur
              </Button>
            ) : null}
            <Button variant="outline" size="sm" onClick={logout}>
              <LogOut className="w-4 h-4 mr-2" />
              Çıkış
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-slate-800 mb-2">Hoş Geldiniz, {user.name}</h2>
          <p className="text-slate-600">Kişisel gelişim yolculuğunuz burada başlıyor</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Quick Actions */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card 
                className="shadow-lg border-none hover:shadow-xl transition-shadow cursor-pointer"
                onClick={() => navigate('/assessment/intro')}
              >
                <CardHeader>
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center mb-3">
                    <BarChart3 className="w-6 h-6 text-white" />
                  </div>
                  <CardTitle className="text-lg">Mizaç Testi</CardTitle>
                  <CardDescription>
                    {hasAssessment ? 'Testi tekrar al' : 'Mizaç testini şimdi başlat'}
                  </CardDescription>
                </CardHeader>
              </Card>

              <Card 
                className={`shadow-lg border-none hover:shadow-xl transition-shadow ${!hasAssessment ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                onClick={() => hasAssessment && report && navigate(`/report/${report.id}`)}
              >
                <CardHeader>
                  <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center mb-3">
                    <FileText className="w-6 h-6 text-white" />
                  </div>
                  <CardTitle className="text-lg">Raporumu Gör</CardTitle>
                  <CardDescription>
                    {hasAssessment ? 'Mizaç raporunu incele' : 'Henüz rapor yok'}
                  </CardDescription>
                </CardHeader>
              </Card>

              <Card 
                className="shadow-lg border-none hover:shadow-xl transition-shadow cursor-pointer"
                onClick={() => navigate('/coach')}
              >
                <CardHeader>
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center mb-3">
                    <MessageSquare className="w-6 h-6 text-white" />
                  </div>
                  <CardTitle className="text-lg">AI Koçum</CardTitle>
                  <CardDescription>Kişiye özel mentorluk al</CardDescription>
                </CardHeader>
              </Card>

              <Card className="shadow-lg border-none hover:shadow-xl transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl flex items-center justify-center mb-3">
                    <Award className="w-6 h-6 text-white" />
                  </div>
                  <CardTitle className="text-lg">Gelişim</CardTitle>
                  <CardDescription>Güçlü yönlerini keşfet</CardDescription>
                </CardHeader>
              </Card>
            </div>

            {/* Report Summary */}
            {hasAssessment && report && (
              <Card className="shadow-xl border-none bg-gradient-to-br from-blue-50 to-emerald-50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <User className="w-5 h-5 text-blue-600" />
                    Mizaç Profilin
                  </CardTitle>
                  <CardDescription>Kişiliğin hakkında önemli bilgiler</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-semibold text-slate-800 mb-2">Birincil Mizaç Tipin</h4>
                      <Badge className="bg-blue-100 text-blue-700 text-base px-4 py-2">
                        {report.content_json?.primary_temperament || 'Analiz ediliyor'}
                      </Badge>
                    </div>
                    
                    {report.content_json?.strengths && (
                      <div>
                        <h4 className="font-semibold text-slate-800 mb-2">Güçlü Yönlerim</h4>
                        <div className="flex flex-wrap gap-2">
                          {report.content_json.strengths.slice(0, 5).map((strength, idx) => (
                            <Badge key={idx} variant="outline" className="bg-white">
                              {strength}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    <Button 
                      className="bg-gradient-to-r from-blue-600 to-emerald-600"
                      onClick={() => navigate(`/report/${report.id}`)}
                    >
                      Detaylı Raporu Gör
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Getting Started */}
            {!hasAssessment && (
              <Card className="shadow-xl border-none">
                <CardHeader>
                  <CardTitle>Başlangıç Adımları</CardTitle>
                  <CardDescription>Mizaç360'tan en iyi şekilde yararlanmak için</CardDescription>
                </CardHeader>
                <CardContent>
                  <ol className="list-decimal list-inside space-y-3 text-slate-700">
                    <li className="font-medium">Mizaç testini tamamlayın (yaklaşık 10 dakika)</li>
                    <li>Kişiselleştirilmiş raporunuzu inceleyin</li>
                    <li>AI Koçunuzla sohbet ederek derinlemesine anlayış kazanın</li>
                    <li>Güçlü yönlerinizi iş hayatınızda kullanmaya başlayın</li>
                  </ol>
                  <Button 
                    className="mt-6 bg-gradient-to-r from-blue-600 to-emerald-600"
                    onClick={() => navigate('/assessment/intro')}
                  >
                    Mizaç Testine Başla
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
